import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminSlideshow.css";

/* webpack require.context for slideshow images */
let SLIDESHOW_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/slideshow",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  SLIDESHOW_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* running outside CRA — images will fall back to API URL */
}

function slideshowImg(filename) {
  if (!filename) return null;
  return SLIDESHOW_IMAGES[filename] ?? `${API_BASE}/uploads/slideshow/${filename}`;
}

const EMPTY_FORM = {
  title:       "",
  subtitle:    "",
  button_text: "",
  button_link: "",
  order:       "",
  active:      1,
  image:       null, // new file upload
};

/* ─────────────────────────────────
   Dot-menu (3 vertical dots)
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="asl-dot-menu" ref={ref}>
      <button
        className="asl-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="asl-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="asl-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="asl-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="asl-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="asl-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`asl-badge ${isActive ? "asl-badge--active" : "asl-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ slide, onClose }) {
  if (!slide) return null;

  return (
    <div className="asl-modal-overlay" onClick={onClose}>
      <div className="asl-modal asl-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="asl-modal__header">
          <div className="asl-modal__title-row">
            <span className="asl-modal__tag">VIEW</span>
            <h2 className="asl-modal__title">{slide.title || "Slide"}</h2>
          </div>
          <button className="asl-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asl-modal__body asl-modal__body--view">
          {slide.image_filename ? (
            <img
              src={slideshowImg(slide.image_filename)}
              alt={slide.title || ""}
              className="asl-view-image"
            />
          ) : (
            <div style={{ background: "#f3f4f6", height: 160, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, color: "#ccc", fontSize: "2rem" }}>
              🖼
            </div>
          )}

          <div className="asl-view-grid">
            {[
              ["Title",       slide.title    || "—"],
              ["Subtitle",    slide.subtitle  || "—"],
              ["Button Text", slide.button_text || "—"],
              ["Button Link", slide.button_link || "—"],
              ["Order",       slide.order ?? "—"],
              ["Status",      Number(slide.active) === 1 ? "Active" : "Inactive"],
            ].map(([label, val]) => (
              <div key={label} className="asl-view-row">
                <span className="asl-view-row__label">{label}</span>
                <span className="asl-view-row__val">{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, slide, onClose, onSaved }) {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [preview,  setPreview]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  /* Populate when editing */
  useEffect(() => {
    if (mode === "edit" && slide) {
      setForm({
        title:       slide.title       || "",
        subtitle:    slide.subtitle    || "",
        button_text: slide.button_text || "",
        button_link: slide.button_link || "",
        order:       slide.order       ?? "",
        active:      Number(slide.active) === 1 ? 1 : 0,
        image:       null,
      });
      setPreview(slide.image_filename ? slideshowImg(slide.image_filename) : null);
    }
  }, [mode, slide]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleFile = (file) => {
    if (!file) return;
    set("image", file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) return setError("Slide title is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") fd.append(k, v);
      });

      if (mode === "create") {
        await apiFetch("/api/admin/slideshow", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/slideshow/${slide.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asl-modal-overlay" onClick={onClose}>
      <div className="asl-modal asl-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="asl-modal__header">
          <div className="asl-modal__title-row">
            <span className="asl-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="asl-modal__title">
              {mode === "create" ? "Add Slide" : "Edit Slide"}
            </h2>
          </div>
          <button className="asl-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asl-modal__body asl-modal__body--form">
          {error && <div className="asl-modal__error">⚠ {error}</div>}

          {/* ── Section: Slide Content ── */}
          <div className="asl-form-section">
            <h3 className="asl-form-section__title">Slide Content</h3>
            <div className="asl-form-grid">
              <div className="asl-form-field asl-form-field--full">
                <label>Title <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Premium Auto Parts"
                />
              </div>
              <div className="asl-form-field asl-form-field--full">
                <label>Subtitle</label>
                <input
                  type="text"
                  value={form.subtitle}
                  onChange={(e) => set("subtitle", e.target.value)}
                  placeholder="e.g. Quality you can trust, prices you'll love"
                />
              </div>
              <div className="asl-form-field">
                <label>Button Text</label>
                <input
                  type="text"
                  value={form.button_text}
                  onChange={(e) => set("button_text", e.target.value)}
                  placeholder="e.g. Shop Now"
                />
              </div>
              <div className="asl-form-field">
                <label>Button Link</label>
                <input
                  type="text"
                  value={form.button_link}
                  onChange={(e) => set("button_link", e.target.value)}
                  placeholder="e.g. /shop"
                />
              </div>
              <div className="asl-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Image ── */}
          <div className="asl-form-section">
            <h3 className="asl-form-section__title">Slide Image</h3>
            <label className="asl-image-upload">
              {preview ? (
                <img src={preview} alt="" className="asl-image-upload__preview" />
              ) : (
                <div className="asl-image-upload__placeholder">
                  <span>🖼</span>
                  <span>Click to upload image</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <span className="asl-image-upload__label">
                {preview ? "Click to change image" : "Upload Image"}
              </span>
            </label>
            <p className="asl-form-hint">
              Images are saved to <code>src/assets/uploads/slideshow/</code>. Leave blank to keep existing.
            </p>
          </div>

          {/* ── Section: Options ── */}
          <div className="asl-form-section">
            <h3 className="asl-form-section__title">Options</h3>
            <div className="asl-form-grid asl-form-grid--checks">
              <label className="asl-check">
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible on site)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="asl-modal__footer">
          <button className="asl-btn asl-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="asl-btn asl-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Slide" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ slide, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/slideshow/${slide.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="asl-modal-overlay" onClick={onClose}>
      <div className="asl-modal asl-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="asl-delete__icon">🗑</div>
        <h2 className="asl-delete__title">Delete Slide?</h2>
        <p className="asl-delete__body">
          You are about to permanently delete{" "}
          <strong>{slide?.title || "this slide"}</strong>. This action cannot be undone.
        </p>
        {error && <div className="asl-modal__error">⚠ {error}</div>}
        <div className="asl-delete__actions">
          <button className="asl-btn asl-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="asl-btn asl-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminSlideshow() {
  const [slides,     setSlides]     = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,     setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort,       setSort]       = useState("order_asc");
  const [page,       setPage]       = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null); // null | "view" | "create" | "edit" | "delete"
  const [target, setTarget] = useState(null);

  /* ── Load slides ── */
  const loadSlides = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/slideshow?${q}`)
      .then((data) => {
        setSlides(data.slides       || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, sort, page]);

  useEffect(() => { loadSlides(); }, [loadSlides]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, slide = null) => { setModal(type); setTarget(slide); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadSlides(); };

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Slideshow">

      {error && (
        <div className="asl-error-bar">⚠ {error}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="asl-toolbar">
        <div className="asl-toolbar__left">
          <div className="asl-search-wrap">
            <span className="asl-search-wrap__icon">🔍</span>
            <input
              className="asl-search"
              type="text"
              placeholder="Search slides by title or subtitle…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button className="asl-search-wrap__clear" onClick={() => { setSearch(""); setPage(1); }}>
                ✕
              </button>
            )}
          </div>

          <select
            className="asl-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="asl-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
          </select>
        </div>

        <div className="asl-toolbar__right">
          <span className="asl-toolbar__count">
            {loading ? "…" : `${pagination.total} slide${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="asl-btn-primary" onClick={() => openModal("create")}>
            + Add Slide
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="asl-table-skeleton" />
      ) : slides.length === 0 ? (
        <div className="asl-empty">
          <span>🖼</span>
          <p>No slides found.</p>
          {(search || statusFilter) && (
            <button
              className="asl-btn asl-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="asl-table-wrap">
          <table className="asl-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title / Subtitle</th>
                <th>Button</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slides.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.image_filename ? (
                      <img src={slideshowImg(s.image_filename)} alt="" className="asl-img-thumb" />
                    ) : (
                      <span className="asl-no-img">—</span>
                    )}
                  </td>
                  <td>
                    <div className="asl-title-cell">
                      <span className="asl-title-cell__name">{s.title || "—"}</span>
                      {s.subtitle && (
                        <span className="asl-title-cell__subtitle">{s.subtitle}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {s.button_text ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <span style={{ fontSize: ".84rem", fontWeight: 600 }}>{s.button_text}</span>
                        {s.button_link && (
                          <span className="asl-btn-link">{s.button_link}</span>
                        )}
                      </div>
                    ) : (
                      <span className="asl-no-img">—</span>
                    )}
                  </td>
                  <td>
                    <span className="asl-order-badge">{s.order ?? 0}</span>
                  </td>
                  <td>
                    <StatusBadge active={s.active} />
                  </td>
                  <td>
                    <DotMenu
                      onView={()   => openModal("view",   s)}
                      onEdit={()   => openModal("edit",   s)}
                      onDelete={() => openModal("delete", s)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="asl-pagination">
          <button
            className="asl-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`asl-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="asl-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   slide={target}  onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"   onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <FormModal   mode="edit"     slide={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && <DeleteModal slide={target}  onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}